// Text shaping with HarfBuzz, the same way in the browser, the desktop app and headless renders.
// Layout measures with shaped advances, and renderers fill the glyph outlines themselves, so
// kerning, ligatures and every OpenType feature come from the font and look identical
// everywhere (the browser's and Skia's own text engines are no longer involved).
//
// Fonts come from a FontProvider (the host knows where files live). Characters that no loaded
// font has (emoji, scripts the font lacks) are left to the host's text engine as fallback runs.

import type * as HB from 'harfbuzzjs';
import { RTL_CHARS, paragraphLevel } from './bidi.ts';

export interface FontStyleInfo {
  weight: number;
  italic: boolean;
}

export interface FontProvider {
  /** Styles available for a family; null when the shaper can't draw this family. */
  styles(family: string): FontStyleInfo[] | null;
  /** Plain OpenType data for one style (several files for subsetted families), now or later. */
  load(family: string, weight: number, italic: boolean): Uint8Array[] | Promise<Uint8Array[]>;
}

export interface ShapedGlyph {
  face: number;
  gid: number;
  /** UTF-16 offset of the character(s) this glyph shows */
  cluster: number;
  /** pen position (px, y down) relative to the run origin, before letter spacing */
  x: number;
  y: number;
  /** px per em (smaller than the run's size for synthetic small caps) */
  size: number;
  /** synthetic oblique (italic asked for, only an upright face available) */
  skew: number;
}

export interface ShapedText {
  size: number;
  glyphs: ShapedGlyph[];
  /** text no loaded font covers: the host draws it with its own engine */
  fallback: { start: number; end: number; x: number }[];
  /** total advance (px) without letter spacing */
  width: number;
  /** right-to-left text: glyphs are in visual order, so clusters run backwards */
  rtl: boolean;
}

export interface TextShaper {
  shape(text: string, family: string, weight: number, italic: boolean, size: number, features: Record<string, boolean>, fallbackWidth: (s: string) => number): ShapedText | null;
  /** Outline in em units (y down): ops 0 M x y, 1 L x y, 2 Q x1 y1 x y, 3 C x1 y1 x2 y2 x y, 4 Z. */
  outline(face: number, gid: number): Float32Array | null;
  /** OpenType features the font has (null: not loaded yet, or a family the shaper doesn't have). */
  featuresFor?(family: string, weight: number, italic: boolean): Set<string> | null;
}

interface LoadedFace {
  face: HB.Face;
  font: HB.Font;
  /** variation axes (variable fonts): tag → range */
  axes: Record<string, { min: number; default: number; max: number }>;
  /** instances of this variable face by settings ("wght=700,ital=1") → face index */
  instances: Map<string, number>;
  upem: number;
  cmap: Set<number>;
  /** GSUB/GPOS feature tags the face has */
  features: Set<string>;
  outlines: Map<number, Float32Array | null>;
}

type Slot = { faces: number[]; italic: boolean } | 'loading' | 'failed';

const OPS: Record<string, number> = { M: 0, L: 1, Q: 2, C: 3, Z: 4 };
const SYNTH_ITALIC = 0.2;
const SMALL_CAPS = 0.7;
// combining marks, joiners and variation selectors stay with the character before them
const CLINGS = /[\p{M}\u200c\u200d\ufe00-\ufe0f]|[\u{e0100}-\u{e01ef}]/u;

export class HarfBuzzShaper implements TextShaper {
  private faces: LoadedFace[] = [];
  private slots = new Map<string, Slot>();
  private pending = new Map<string, Promise<void>>();
  private cache = new Map<string, ShapedText | null>();
  private featureLists = new Map<string, HB.Feature[]>();
  private buffer: HB.Buffer;

  constructor(
    private hb: typeof HB,
    private provider: FontProvider,
    /** Called when fonts that were loading are ready (lay text out again). */
    private onLoad: () => void = () => {},
  ) {
    this.buffer = new hb.Buffer();
  }

  /** Load a style ahead of use; resolves once it can be shaped (or failed to load). */
  preload(family: string, weight: number, italic: boolean): Promise<void> {
    this.slot(family, weight, italic);
    const styles = this.provider.styles(family);
    if (!styles?.length) return Promise.resolve();
    const pick = matchStyle(styles, weight, italic);
    return this.pending.get(`${family.toLowerCase()}|${pick.weight}|${pick.italic}`) ?? Promise.resolve();
  }

  /** Forget cached shapes and style choices (after fonts were added). */
  invalidate() {
    this.cache.clear();
    for (const [k, v] of this.slots) if (v === 'failed') this.slots.delete(k);
  }

  shape(text: string, family: string, weight: number, italic: boolean, size: number, features: Record<string, boolean>, fallbackWidth: (s: string) => number): ShapedText | null {
    const fkey = featureKey(features);
    const key = `${family}\u0001${weight}\u0001${italic ? 1 : 0}\u0001${size}\u0001${fkey}\u0001${text}`;
    const hit = this.cache.get(key);
    if (hit !== undefined) return hit;
    const slot = this.slot(family, weight, italic);
    if (!slot || typeof slot !== 'object') return null; // loading (not cached: it will be ready soon) or unavailable
    // variable fonts: the asked-for weight (and italic, through an ital or slnt axis) as an instance
    const wantItalic = italic && !slot.italic;
    const inst = slot.faces.map((id) => this.instance(id, weight, wantItalic));
    const skew = wantItalic && !inst.every((i) => i.italic) ? SYNTH_ITALIC : 0;
    const out = this.shapeWith(inst.map((i) => i.id), text, size, skew, fkey, features, fallbackWidth);
    if (this.cache.size > 20000) this.cache.clear();
    this.cache.set(key, out);
    return out;
  }

  /** OpenType features the font for this style has (null until it has loaded, or unknown family). */
  featuresFor(family: string, weight: number, italic: boolean): Set<string> | null {
    const slot = this.slot(family, weight, italic);
    if (!slot || typeof slot !== 'object') return null;
    const out = new Set<string>();
    for (const id of slot.faces) for (const f of this.faces[id].features) out.add(f);
    return out;
  }

  /** Variation axes of the font for this style ({} for static fonts; null until loaded). */
  axesFor(family: string, weight: number, italic: boolean): LoadedFace['axes'] | null {
    const slot = this.slot(family, weight, italic);
    if (!slot || typeof slot !== 'object') return null;
    return Object.assign({}, ...slot.faces.map((id) => this.faces[id].axes));
  }

  private instance(id: number, weight: number, wantItalic: boolean): { id: number; italic: boolean } {
    const f = this.faces[id];
    const ax = f.axes;
    const vars: [string, number][] = [];
    if (ax.wght) vars.push(['wght', Math.max(ax.wght.min, Math.min(ax.wght.max, weight))]);
    let italic = false;
    if (wantItalic && ax.ital) (vars.push(['ital', ax.ital.max]), (italic = true));
    else if (wantItalic && ax.slnt) (vars.push(['slnt', Math.max(ax.slnt.min, Math.min(ax.slnt.max, -10))]), (italic = true));
    if (!vars.length) return { id, italic: false };
    const key = vars.map(([t, v]) => `${t}=${v}`).join(',');
    let inst = f.instances.get(key);
    if (inst === undefined) {
      const font = new this.hb.Font(f.face);
      font.setVariations(vars.map(([t, v]) => new this.hb.Variation(t, v)));
      this.faces.push({ ...f, font, outlines: new Map() });
      inst = this.faces.length - 1;
      f.instances.set(key, inst);
    }
    return { id: inst, italic };
  }

  outline(face: number, gid: number): Float32Array | null {
    const f = this.faces[face];
    if (!f) return null;
    let o = f.outlines.get(gid);
    if (o === undefined) {
      const cmds = f.font.glyphToJson(gid);
      let n = 0;
      for (const c of cmds) n += 1 + c.values.length;
      o = n ? new Float32Array(n) : null;
      if (o) {
        let i = 0;
        const k = 1 / f.upem;
        for (const c of cmds) {
          o[i++] = OPS[c.type] ?? 4;
          // font units, y up → em units, y down
          for (let j = 0; j < c.values.length; j += 2) {
            o[i++] = c.values[j] * k;
            o[i++] = -c.values[j + 1] * k;
          }
        }
      }
      f.outlines.set(gid, o);
    }
    return o;
  }

  private slot(family: string, weight: number, italic: boolean): Slot | null {
    const styles = this.provider.styles(family);
    if (!styles?.length) return null;
    const pick = matchStyle(styles, weight, italic);
    const key = `${family.toLowerCase()}|${pick.weight}|${pick.italic}`;
    const have = this.slots.get(key);
    if (have) return have;
    let loaded: Uint8Array[] | Promise<Uint8Array[]>;
    try {
      loaded = this.provider.load(family, pick.weight, pick.italic);
    } catch {
      this.slots.set(key, 'failed');
      return 'failed';
    }
    if (Array.isArray(loaded)) {
      const s = this.install(loaded, pick.italic);
      this.slots.set(key, s);
      return s;
    }
    this.slots.set(key, 'loading');
    const done = loaded.then(
      (files) => {
        this.slots.set(key, this.install(files, pick.italic));
        this.cache.clear();
        this.onLoad();
      },
      () => void this.slots.set(key, 'failed'),
    );
    this.pending.set(key, done);
    void done.finally(() => this.pending.delete(key));
    return 'loading';
  }

  private install(files: Uint8Array[], italic: boolean): Slot {
    const ids: number[] = [];
    for (const bytes of files) {
      try {
        const face = new this.hb.Face(new this.hb.Blob(bytes));
        const font = new this.hb.Font(face);
        const features = new Set([...face.getTableFeatureTags('GSUB'), ...face.getTableFeatureTags('GPOS')]);
        const axes: LoadedFace['axes'] = {};
        for (const [tag, a] of Object.entries(face.getAxisInfos())) axes[tag] = { min: a.min, default: a.default, max: a.max };
        this.faces.push({ face, font, axes, instances: new Map(), upem: face.upem || 1000, cmap: new Set(face.collectUnicodes()), features, outlines: new Map() });
        ids.push(this.faces.length - 1);
      } catch {
        /* skip a file HarfBuzz can't read */
      }
    }
    return ids.length ? { faces: ids, italic } : 'failed';
  }

  private features(fkey: string, features: Record<string, boolean>): HB.Feature[] {
    let list = this.featureLists.get(fkey);
    if (!list) {
      list = [];
      for (const [tag, on] of Object.entries(features)) {
        if (!/^[a-z0-9 ]{4}$/i.test(tag)) continue;
        list.push(new this.hb.Feature(tag.toLowerCase(), on ? 1 : 0));
        // "ligatures off" also turns off the contextual ones, as in CSS
        if (tag.toLowerCase() === 'liga' && !on) list.push(new this.hb.Feature('clig', 0));
      }
      this.featureLists.set(fkey, list);
    }
    return list;
  }

  private shapeWith(faceIds: number[], text: string, size: number, skew: number, fkey: string, features: Record<string, boolean>, fallbackWidth: (s: string) => number): ShapedText {
    // split into runs by the first face that has each character (keeping the current face for
    // characters it also has, like spaces between words of another script)
    const runs: [number, number, number][] = [];
    let cur = -2;
    for (let i = 0; i < text.length; ) {
      const cp = text.codePointAt(i)!;
      const len = cp > 0xffff ? 2 : 1;
      let f: number;
      if (cur !== -2 && (CLINGS.test(String.fromCodePoint(cp)) || (cur >= 0 && this.faces[cur].cmap.has(cp)))) f = cur;
      else f = faceIds.find((id) => this.faces[id].cmap.has(cp)) ?? -1;
      if (f !== cur) runs.push([f, i, i + len]);
      else runs[runs.length - 1][2] = i + len;
      cur = f;
      i += len;
    }
    const glyphs: ShapedGlyph[] = [];
    const fallback: ShapedText['fallback'] = [];
    let pen = 0;
    const feats = this.features(fkey, features);
    // right-to-left text (callers pass one direction at a time): font runs go right to left too
    const rtl = RTL_CHARS.test(text) && paragraphLevel(text) === 1;
    if (rtl) runs.reverse();
    for (const [face, a, b] of runs) {
      if (face < 0) {
        fallback.push({ start: a, end: b, x: pen });
        pen += fallbackWidth(text.slice(a, b));
        continue;
      }
      const f = this.faces[face];
      const segment = (t: string, s: number, e: number, sz: number) => {
        const k = sz / f.upem;
        const buf = this.buffer;
        buf.reset();
        buf.addText(t, s, e - s);
        buf.guessSegmentProperties();
        this.hb.shape(f.font, buf, feats);
        const infos = buf.getGlyphInfos();
        const pos = buf.getGlyphPositions();
        for (let g = 0; g < infos.length; g++) {
          const p = pos[g];
          glyphs.push({ face, gid: infos[g].codepoint, cluster: infos[g].cluster, x: pen + p.xOffset * k, y: -p.yOffset * k, size: sz, skew });
          pen += p.xAdvance * k;
        }
      };
      if (!features.smcp || f.features.has('smcp')) segment(text, a, b, size);
      else {
        // the font has no small caps: lowercase letters become capitals at 70% (as browsers do)
        const re = /\p{Ll}+|\P{Ll}+/gu;
        re.lastIndex = 0;
        const part = text.slice(a, b);
        for (const m of part.matchAll(re)) {
          const s = a + m.index!,
            e = s + m[0].length;
          const up = m[0].toUpperCase();
          if (/\p{Ll}/u.test(m[0]) && up.length === m[0].length) segment(text.slice(0, s) + up + text.slice(e), s, e, size * SMALL_CAPS);
          else segment(text, s, e, size);
        }
      }
    }
    return { size, glyphs, fallback, width: pen, rtl };
  }
}

/** CSS-style font matching: the asked-for style first, then the closest weight. */
export function matchStyle(styles: FontStyleInfo[], weight: number, italic: boolean): FontStyleInfo {
  const same = styles.filter((s) => s.italic === italic);
  const pool = same.length ? same : styles;
  let best = pool[0];
  let bestD = Infinity;
  for (const s of pool) {
    // ties go lighter for normal-ish weights, heavier for bold ones (as CSS does)
    const d = Math.abs(s.weight - weight) + (weight <= 500 ? (s.weight > weight ? 0.5 : 0) : s.weight < weight ? 0.5 : 0);
    if (d < bestD) (bestD = d), (best = s);
  }
  return best;
}

function featureKey(f: Record<string, boolean>): string {
  let k = '';
  for (const t of Object.keys(f).sort()) k += `${t}${f[t] ? 1 : 0},`;
  return k;
}

/**
 * The bundled Inter is 3.19 (npm inter-ui), the version Figma ships: its advances and kerning
 * match Figma's text widths exactly, where Inter 4 is up to 3% off per glyph.
 */
const INTER_NAMES: Record<number, string> = { 100: 'Thin', 200: 'ExtraLight', 300: 'Light', 400: 'Regular', 500: 'Medium', 600: 'SemiBold', 700: 'Bold', 800: 'ExtraBold', 900: 'Black' };
export const INTER_WEIGHTS = Object.keys(INTER_NAMES).map(Number);
/** File base name of a bundled Inter style ("Inter-SemiBoldItalic"), null if there's none. */
export function interFile(weight: number, italic: boolean): string | null {
  const n = INTER_NAMES[weight];
  if (!n) return null;
  return italic ? (weight === 400 ? 'Inter-Italic' : `Inter-${n}Italic`) : `Inter-${n}`;
}
